import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Phone, Camera } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fullUrl = (u) =>
  u && u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;

const CTA = {
  painting: { title: "Ready to refresh your home?", text: "Interior, exterior, decks and log-home staining done right." },
  decks: { title: "Dreaming of a new deck?", text: "Custom deck builds and safe, code-ready railing for your mountain views." },
  pavers: { title: "Want a beautiful new patio?", text: "Durable paver patios, walkways and driveways built for the mountains." },
  roof: { title: "Want to extend your roof's life?", text: "Protective roof coating and repairs that save you thousands over replacement." },
  bathroom: { title: "Planning a bathroom remodel?", text: "Modern tile showers, vanities and fixtures built to last." },
  restoration: { title: "Damage at your home?", text: "We respond same-day, 24/7. Get help now." },
};
const DEFAULT_CTA = { title: "Need this at your home?", text: "Call for fast, honest service and a free estimate." };

export const Gallery = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(null);
  const pending = useRef(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(({ data }) => {
      const withItems = data.categories.filter((c) =>
        data.items.some((it) => it.category === c.key)
      );
      setCategories(withItems);
      setItems(data.items);
      if (pending.current && withItems.some((c) => c.key === pending.current)) {
        setTab(pending.current);
      } else if (withItems.length) {
        setTab(withItems[0].key);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const key = e.detail;
      pending.current = key;
      if (categories.some((c) => c.key === key)) setTab(key);
    };
    window.addEventListener("select-gallery-category", handler);
    return () => window.removeEventListener("select-gallery-category", handler);
  }, [categories]);

  if (!tab) return null;
  const shown = items.filter((it) => it.category === tab);
  const cta = CTA[tab] || DEFAULT_CTA;

  return (
    <section id="work" data-testid="gallery-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-heading font-bold uppercase tracking-[0.2em] text-[#C8102E] text-sm">Real Jobs</p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <h2 className="mt-2 font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-[#0E2A56]">
            Our Recent Work
          </h2>
          <a href={`tel:${PHONE_TEL}`} data-testid="gallery-call-button" className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9E0C24] text-white font-heading font-bold uppercase tracking-wide px-5 py-3 transition-colors self-start">
            <Phone className="h-5 w-5" strokeWidth={2.5} /> {PHONE_DISPLAY}
          </a>
        </div>
        <p className="mt-4 max-w-2xl text-lg text-slate-700 font-medium">
          Actual projects handled by our crew across North Georgia — from log-home staining
          and exterior painting to emergency water damage drying, tear-outs and rebuilds.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          {categories.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`gallery-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`font-heading font-bold uppercase tracking-wide px-5 py-3 border-2 transition-colors ${
                  active ? "bg-[#0E2A56] text-white border-[#0E2A56]" : "bg-white text-[#0E2A56] border-[#0E2A56] hover:bg-[#F6F5F0]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((g, i) => (
            <figure
              key={g.id}
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden border-2 border-[#0E2A56] animate-fade-up ${
                i === 0 ? "sm:col-span-2 lg:row-span-2 lg:col-span-1" : ""
              }`}
            >
              <img src={fullUrl(g.url)} alt={`Journey's Handyman project — ${g.caption}`} className="w-full h-64 sm:h-72 object-cover group-hover:scale-105 transition-transform duration-300" />
              {g.caption && (
                <figcaption className="absolute inset-x-0 bottom-0 bg-[#0A1E3F]/90 text-white px-4 py-3 flex items-start gap-2">
                  <Camera className="h-4 w-4 mt-0.5 text-[#C8102E] shrink-0" strokeWidth={2.5} />
                  <span className="font-medium text-sm leading-snug">{g.caption}</span>
                </figcaption>
              )}
            </figure>
          ))}

          <a href="#quote" data-testid="gallery-cta" className="flex flex-col items-center justify-center text-center bg-[#0E2A56] hover:bg-[#0A1E3F] text-white border-2 border-[#0E2A56] p-8 transition-colors min-h-[16rem]">
            <span className="font-heading text-3xl font-extrabold uppercase tracking-tight leading-none">{cta.title}</span>
            <span className="mt-3 text-white/80">{cta.text}</span>
            <span className="mt-5 inline-flex items-center gap-2 font-heading font-bold uppercase tracking-wide text-[#C8102E]">Request a free quote →</span>
          </a>
        </div>
      </div>
    </section>
  );
};
